import React, { useState } from 'react';
import { CreditCard, Smartphone, Plus, Trash2, Star } from 'lucide-react';

const PaymentMethods = ({ methods, onAdd, onRemove, onSetDefault }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [addType, setAddType] = useState('card');
  const [formData, setFormData] = useState({ cardNumber: '', expiry: '', name: '', mpesa: '' });
  const [saving, setSaving] = useState(false);

  const defaultMethods = methods?.length > 0 ? methods : [
    { id: 'demo-1', type: 'mpesa', label: 'M-Pesa', detail: '+254 7XX XXX XXX', isDefault: true },
  ];

  const handleAdd = async () => {
    setSaving(true);
    try {
      const newMethod = addType === 'card'
        ? { type: 'card', label: `•••• ${formData?.cardNumber?.slice(-4)}`, detail: formData?.expiry, name: formData?.name }
        : { type: 'mpesa', label: 'M-Pesa', detail: formData?.mpesa };
      await onAdd?.(newMethod);
      setShowAddForm(false);
      setFormData({ cardNumber: '', expiry: '', name: '', mpesa: '' });
    } catch (err) {
      console.error('Add payment method failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-[#1a365d]" />
          <h3 className="font-semibold text-[#1a365d]">Payment Methods</h3>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#ffd700] text-[#1a365d] rounded-lg text-xs font-semibold hover:bg-[#fbbf24] transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add New
        </button>
      </div>
      <div className="p-5 space-y-3">
        {defaultMethods?.map((method) => (
          <div
            key={method?.id}
            className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-colors ${method?.isDefault ? 'border-[#1a365d]/30 bg-[#1a365d]/5' : 'border-slate-100 bg-slate-50'}`}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${method?.type === 'mpesa' ? 'bg-green-100' : 'bg-blue-100'}`}>
              {method?.type === 'mpesa'
                ? <Smartphone className="w-5 h-5 text-green-700" />
                : <CreditCard className="w-5 h-5 text-blue-700" />
              }
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-slate-800">{method?.label}</p>
                {method?.isDefault && (
                  <span className="px-1.5 py-0.5 bg-[#ffd700] text-[#1a365d] text-xs font-bold rounded">Default</span>
                )}
              </div>
              <p className="text-xs text-slate-500">{method?.detail}</p>
            </div>
            <div className="flex items-center gap-1.5">
              {!method?.isDefault && (
                <button
                  onClick={() => onSetDefault?.(method?.id)}
                  className="p-1.5 text-slate-400 hover:text-[#1a365d] hover:bg-slate-100 rounded-lg transition-colors"
                  title="Set as default"
                >
                  <Star className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={() => onRemove?.(method?.id)}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Remove"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}

        {showAddForm && (
          <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 space-y-3">
            <div className="flex gap-2">
              {['card', 'mpesa']?.map(t => (
                <button
                  key={t}
                  onClick={() => setAddType(t)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${addType === t ? 'bg-[#1a365d] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                >
                  {t === 'card' ? '💳 Card' : '📱 M-Pesa'}
                </button>
              ))}
            </div>
            {addType === 'card' ? (
              <>
                <input
                  type="text"
                  placeholder="Card number"
                  maxLength={16}
                  value={formData?.cardNumber}
                  onChange={e => setFormData(p => ({ ...p, cardNumber: e?.target?.value?.replace(/\D/g, '') }))}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1a365d]/30 focus:border-[#1a365d]"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="MM/YY"
                    maxLength={5}
                    value={formData?.expiry}
                    onChange={e => setFormData(p => ({ ...p, expiry: e?.target?.value }))}
                    className="text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1a365d]/30 focus:border-[#1a365d]"
                  />
                  <input
                    type="text"
                    placeholder="Cardholder name"
                    value={formData?.name}
                    onChange={e => setFormData(p => ({ ...p, name: e?.target?.value }))}
                    className="text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1a365d]/30 focus:border-[#1a365d]"
                  />
                </div>
              </>
            ) : (
              <input
                type="tel"
                placeholder="M-Pesa number (e.g. +254 7XX XXX XXX)"
                value={formData?.mpesa}
                onChange={e => setFormData(p => ({ ...p, mpesa: e?.target?.value }))}
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1a365d]/30 focus:border-[#1a365d]"
              />
            )}
            <div className="flex gap-2">
              <button onClick={handleAdd} disabled={saving} className="flex-1 py-2 bg-[#1a365d] text-white text-sm font-medium rounded-lg hover:bg-[#2d5a87] transition-colors disabled:opacity-50">
                {saving ? 'Adding...' : 'Add Method'}
              </button>
              <button onClick={() => setShowAddForm(false)} className="px-4 py-2 bg-slate-100 text-slate-600 text-sm rounded-lg hover:bg-slate-200 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentMethods;
